import * as React from 'react';
import { useSnackbar } from 'notistack';
import useAuth from 'hooks/useAuth';
import axios from '../../utils/axios';
import { HistoricoGPS } from '../historicoGPS';
import { Num, PillStatus, SeletorStatus, statusTexto } from './index';
import ModalPassos, { PassoModal } from './ModalPassos';
import { Campo, classesEntrada } from './Modal';
import { cn } from '../../lib/utils';
import { AutoCompleteMultiplo, AutoCompleteUnico, buscarCrs, buscarPaginado } from './AutoComplete';

/* ============================================================================
   Cadastro de usuario em MODAL DE PASSOS (o assistente de cartoes aprovado).

   Substitui a pagina /dashboard/users/new/:codigo para criar e editar. A pagina
   antiga continua no ar e nao foi tocada.

   NADA DO FORMULARIO ANTIGO FICOU DE FORA - conferido campo por campo em
   pages/dashboard/users/new/[codigo].tsx:
     nome, email, usuarioRegionais (multi), cr, status, perfil, status_pendente,
     a aba de troca de senha e a aba de historico.

   TRES REGRAS DO FORMULARIO ANTIGO QUE CONTINUAM VALENDO, porque sao de negocio
   e nao de tela:

   1. REGIONAL E OBRIGATORIA. Sem ela o usuario entra e nao ve nada. O antigo
      barrava no submit com um aviso; aqui o cartao fica sem ✓ e o botao de
      finalizar diz o que falta - o usuario descobre ANTES de preencher o resto.

   2. `dataCampo` E A TRILHA DE AUDITORIA. O backend grava o diff campo a campo
      (`RegisterHistorico`). Se a tela nao montar essa lista, a alteracao entra no
      banco SEM historico - por isso o mesmo calculo esta aqui, comparando com o
      registro carregado.

   3. PERFIL MASTER IMPLICA `versaoDesenvolvedor = true`, para compatibilidade com
      consultas antigas (notificacoes e afins). Nao e enfeite: tirar isso quebra
      quem depende da flag.

   E o teto de perfil segue o de quem esta logado: Master atribui qualquer perfil,
   Administrador ate ADMINISTRADOR, os demais ate GERENTE. O backend tambem valida
   (users.service.ts, `nivelDe`) - isto aqui e conveniencia.
   ============================================================================ */

type Regional = { codigo: number; nome: string };
type Cr = { codigo: string; descricaoCompleta?: string; descricao?: string };

type Formulario = {
  nome: string;
  email: string;
  status: string;
  perfil: string;
  cr: Cr | null;
  usuarioRegionais: Regional[];
  status_pendente: boolean;
};

const VAZIO: Formulario = {
  nome: '',
  email: '',
  status: 'ATIVO',
  perfil: 'USUARIO',
  cr: null,
  usuarioRegionais: [],
  status_pendente: false,
};

const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function ModalUsuario({
  aberto,
  codigo,
  onFechar,
  onSalvo,
}: {
  aberto: boolean;
  /** `undefined` ou '0' = criar. Qualquer outro valor = editar. */
  codigo?: string | number;
  onFechar: () => void;
  onSalvo?: () => void;
}) {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const editando = Boolean(codigo && String(codigo) !== '0');

  const [form, setForm] = React.useState<Formulario>(VAZIO);
  const [original, setOriginal] = React.useState<any>({});
  const [salvando, setSalvando] = React.useState(false);
  const [carregando, setCarregando] = React.useState(false);


  const [senhaAtual, setSenhaAtual] = React.useState('');
  const [senhaNova, setSenhaNova] = React.useState('');
  const [senhaConfirmar, setSenhaConfirmar] = React.useState('');
  const [trocandoSenha, setTrocandoSenha] = React.useState(false);

  // Teto de perfil de quem esta logado. Mesma regra do formulario antigo.
  const perfisDisponiveis = React.useMemo(() => {
    const p = Number(user?.perfil);
    if (p === 3) return ['USUARIO', 'GERENTE', 'ADMINISTRADOR', 'MASTER'];
    if (p === 1) return ['USUARIO', 'GERENTE', 'ADMINISTRADOR'];
    return ['USUARIO', 'GERENTE'];
  }, [user?.perfil]);

  // ---------------------------------------------------------------- carga
  React.useEffect(() => {
    if (!aberto) return;

    setForm(VAZIO);
    setOriginal({});
    setSenhaAtual('');
    setSenhaNova('');
    setSenhaConfirmar('');

    if (!editando) return;

    setCarregando(true);
    axios
      .get<any>(`/users/byId/${codigo}`)
      .then((r) => {
        const d = r.data || {};
        setOriginal(d);
        setForm({
          nome: d.nome ?? '',
          email: d.email ?? '',
          status: statusTexto(d.status),
          perfil: d.perfil ?? 'USUARIO',
          cr: d.cr ?? null,
          usuarioRegionais: d.usuarioRegionais ?? [],
          status_pendente: Boolean(d.pendente),
        });
      })
      .catch((e: any) =>
        enqueueSnackbar(e?.response?.data?.message || 'Não foi possível carregar o usuário.', {
          variant: 'error',
        })
      )
      .finally(() => setCarregando(false));
  }, [aberto, codigo, editando, enqueueSnackbar]);

  /* SELETORES PAGINADOS, SO NA LUPA (21/10/2026). Antes o modal pedia 200
     regionais e 50 CRs ao abrir e de novo a cada pausa na digitacao. Mesmos
     endpoints e filtros: regionais so as que QUEM ESTA LOGADO administra
     (`gerente`), igual ao RHFAutoComplete do formulario antigo. */
  const buscarRegionais = React.useMemo(
    () =>
      buscarPaginado(
        (termo, pagina, linhas) =>
          `/regionais/paginado?page=${pagina}&rows=${linhas}&campo=nome&valor=${encodeURIComponent(termo)}` +
          `&direcao=asc&ordenar=nome&gerente=${user?.codigo ?? -1}`,
        (r) => ({ codigo: r.codigo, rotulo: r.nome })
      ),
    [user?.codigo]
  );

  // ------------------------------------------------------------ validacao
  const nomeOk = form.nome.trim().length > 0 && form.nome.trim().length <= 150;
  const emailOk = RE_EMAIL.test(form.email.trim());
  const regionalOk = form.usuarioRegionais.length > 0;

  // --------------------------------------------------------------- salvar
  async function salvar() {
    if (salvando) return;
    setSalvando(true);
    try {
      // Trilha de auditoria: o backend grava este diff campo a campo. Sem ela a
      // alteracao entra no banco sem historico.
      const diff: Array<{ campo: string; antigo: any; novo: any }> = [];
      const cmp = (campo: string, antigo: any, novo: any) => {
        if (String(antigo ?? '') !== String(novo ?? '')) diff.push({ campo, antigo, novo });
      };
      cmp('NOME', original.nome, form.nome);
      cmp('E-MAIL', original.email, form.email);
      cmp('STATUS', original.status, form.status);
      cmp('PERFIL', original.perfil, form.perfil);
      cmp('CR', original.cr?.codigo ?? original.cr, form.cr?.codigo ?? form.cr);
      cmp(
        'STATUS_PENDENTE',
        original.pendente ? 'Sim' : 'Não',
        form.status_pendente ? 'Sim' : 'Não'
      );

      // MASTER implica versaoDesenvolvedor = true (compatibilidade com consultas
      // antigas). Mesma regra do formulario anterior.
      const ehMaster = String(form.perfil).toUpperCase() === 'MASTER';

      const corpo = {
        nome: form.nome.trim(),
        email: form.email.trim(),
        status: form.status,
        perfil: form.perfil,
        versaoDesenvolvedor: ehMaster ? true : Boolean(original.versaoDesenvolvedor),
        cr: form.cr,
        status_pendente: form.status_pendente,
        dataCampo: JSON.stringify(diff),
        usuarioRegionais: form.usuarioRegionais,
        codigo: editando ? original.codigo : undefined,
      };

      if (editando) await axios.put(`/users/${codigo}`, corpo);
      else await axios.post('/users', corpo);

      enqueueSnackbar('Salvo com sucesso', { variant: 'success' });
      onSalvo?.();
      onFechar();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'erro ao salvar no sistema', {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
    } finally {
      setSalvando(false);
    }
  }

  async function alterarSenha() {
    if (!senhaAtual || !senhaNova || !senhaConfirmar) {
      enqueueSnackbar('Preencha todos campos das senhas.', { variant: 'warning' });
      return;
    }
    if (senhaNova !== senhaConfirmar) {
      enqueueSnackbar('Senha nova e confirmar são diferentes.', { variant: 'warning' });
      return;
    }
    setTrocandoSenha(true);
    try {
      await axios.post(`/users/trocasenha/${codigo}`, {
        codigo: original.codigo,
        senhaAtual,
        senhaNova,
      });
      enqueueSnackbar('Alterado com sucesso', { variant: 'success' });
      setSenhaAtual('');
      setSenhaNova('');
      setSenhaConfirmar('');
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Erro ao alterar a senha.', { variant: 'error' });
    } finally {
      setTrocandoSenha(false);
    }
  }

  // --------------------------------------------------------------- cartoes
  const passos: PassoModal[] = [
    {
      id: 'dados',
      titulo: 'Dados',
      grupo: 'Cadastro',
      resumo: form.nome?.trim() || 'sem nome',
      valido: nomeOk && emailOk,
      pendencia: !nomeOk ? 'nome' : 'e-mail válido',
      conteudo: (
        <>
          {/* DUAS COLUNAS. Nome e e-mail sao curtos e sempre preenchidos juntos;
              um embaixo do outro, cada um ocupava uma linha de 1300px de largura
              para receber 30 caracteres. Volta a uma coluna abaixo de `sm`. */}
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo rotulo="Nome" obrigatorio>
              <input
                value={form.nome}
                onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                maxLength={150}
                className={classesEntrada}
              />
            </Campo>

            <Campo
              rotulo="E-mail"
              obrigatorio
              dica="É por aqui que a senha inicial e o link de redefinição chegam."
            >
              <input
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                type="email"
                className={cn(classesEntrada, form.email && !emailOk && 'border-danger')}
              />
              {form.email && !emailOk && (
                <span className="mt-1 block text-[11px] text-danger">E-mail em formato inválido.</span>
              )}
            </Campo>
          </div>

          {/* FICHA DO CADASTRO - so os campos que a API DEVOLVE de fato.

              `/users/byId` responde a entidade inteira (users.service.ts:251), e o
              `createdAt` esta nela - por isso CRIADO EM aparece sem mudar nada no
              servidor.

              O desenho aprovado tinha um quarto campo, ULTIMO ACESSO, e ele NAO
              esta aqui: TAB_USUARIO nao tem coluna de ultimo acesso. Esse dado
              existe no historico (evento de login), e mostra-lo pede uma consulta
              nova no backend. Preferi a ficha com tres campos verdadeiros a quatro
              com um "—" fingindo informacao. */}
          {editando && (
            <div className="overflow-hidden rounded-lg border border-line">
              <div className="border-b border-line bg-surface-2 px-3 py-1.5 text-[11px] font-semibold text-muted-foreground">
                Ficha do cadastro
              </div>
              <div className="grid gap-3 p-3 sm:grid-cols-3">
                <span className="min-w-0">
                  <span className="block text-[10px] font-bold uppercase tracking-[0.07em] text-faint">Código</span>
                  <Num className="block truncate text-[12px]">{original.codigo ?? '—'}</Num>
                </span>
                <span className="min-w-0">
                  <span className="block text-[10px] font-bold uppercase tracking-[0.07em] text-faint">Criado em</span>
                  <Num className="block truncate text-[12px]">
                    {original.createdAt
                      ? new Date(original.createdAt).toLocaleDateString('pt-BR')
                      : '—'}
                  </Num>
                </span>
                <span className="min-w-0">
                  <span className="block text-[10px] font-bold uppercase tracking-[0.07em] text-faint">
                    Cadastro pendente
                  </span>
                  <span className="block truncate text-[12px]">{original.pendente ? 'sim' : 'não'}</span>
                </span>
              </div>
            </div>
          )}
        </>
      ),
    },

    {
      id: 'acesso',
      titulo: 'Perfil e acesso',
      grupo: 'Cadastro',
      resumo: [form.perfil, statusTexto(form.status)].filter(Boolean).join(' · ') || 'não definido',
      // Perfil e status tem valor padrao, entao este bloco nasce valido - o ✓
      // aqui significa "escolhido", nao "preenchido a mao".
      valido: Boolean(form.perfil && form.status),
      pendencia: 'perfil e status',
      conteudo: (
        <>
          <Campo rotulo="Perfil" obrigatorio dica="Decide quem este usuário pode ver e editar.">
            <div className="flex flex-wrap gap-1.5">
              {perfisDisponiveis.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, perfil: p }))}
                  className={cn(
                    'h-8 rounded-lg border px-2.5 text-[12px] font-bold',
                    form.perfil === p
                      ? 'border-primary bg-primary/10 text-ink'
                      : 'border-line text-muted-foreground hover:text-ink'
                  )}
                >
                  {p}
                </button>
              ))}
            </div>
            {String(form.perfil).toUpperCase() === 'MASTER' && (
              <span className="mt-1.5 block text-[11px] text-warn">
                Master enxerga e edita todos os perfis, e recebe automaticamente a marca de
                desenvolvedor.
              </span>
            )}
          </Campo>

          <Campo bloco rotulo="Status">
            <SeletorStatus valor={form.status} onChange={(v) => setForm((f) => ({ ...f, status: v }))} />
            {form.status === 'INATIVO' && (
              <span className="mt-1.5 block text-[11px] text-muted-foreground">
                Inativo não entra no sistema, mas continua no cadastro e no histórico.
              </span>
            )}
          </Campo>

          {/* O campo "cadastro pendente" so aparecia quando o usuario TINHA
              pendencia - mesma regra aqui: sem convite em aberto, o campo nao
              tem o que representar. */}
          {editando && original.pendente && (
            <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-line bg-warn-soft p-3">
              <input
                type="checkbox"
                checked={form.status_pendente}
                onChange={(e) => setForm((f) => ({ ...f, status_pendente: e.target.checked }))}
                className="mt-0.5 h-3.5 w-3.5 accent-[hsl(var(--primary))]"
              />
              <span>
                <span className="block text-[12px] font-semibold text-warn">Cadastro pendente</span>
                <span className="block text-[11px] text-muted-foreground">
                  Convite enviado e primeiro acesso ainda não feito. Desmarcar encerra a pendência.
                </span>
              </span>
            </label>
          )}
        </>
      ),
    },

    {
      id: 'regionais',
      titulo: 'Regionais',
      grupo: 'Cadastro',
      resumo: regionalOk ? `${form.usuarioRegionais.length} selecionada(s)` : 'nenhuma',
      valido: regionalOk,
      pendencia: 'ao menos uma regional',
      conteudo: (
        <>
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[12px] font-semibold">
              Regionais{' '}
              <span className="ml-1 rounded bg-danger-soft px-1.5 py-0.5 text-[10px] font-bold text-danger">
                OBRIGATÓRIO
              </span>
            </span>
            <span className={cn('text-[11px]', regionalOk ? 'text-muted-foreground' : 'text-danger')}>
              {regionalOk ? `${form.usuarioRegionais.length} selecionada(s)` : 'nenhuma'}
            </span>
          </div>

          <AutoCompleteMultiplo
            selecionados={form.usuarioRegionais.map((r: any) => ({ codigo: r.codigo, rotulo: r.nome }))}
            // Mesmo formato de antes no corpo: { codigo, nome } por regional.
            aoMudar={(l) =>
              setForm((f) => ({
                ...f,
                usuarioRegionais: l.map((o) => ({ codigo: Number(o.codigo), nome: o.rotulo })),
              }))
            }
            buscar={buscarRegionais}
            titulo="Selecionar regionais"
            placeholder="Nome da regional (Enter ou lupa)"
            vazio="Nenhuma regional encontrada. Você só pode conceder as que administra."
          />

          <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px] text-muted-foreground">
            Sem regional o usuário entra no sistema e não enxerga grupo, dispositivo nem política.
          </div>
        </>
      ),
    },

    {
      id: 'cr',
      titulo: 'Centro de resultado',
      grupo: 'Cadastro',
      resumo: form.cr ? String(form.cr.codigo ?? '') : 'não definido',
      // Opcional de verdade: o formulario antigo nunca exigiu CR.
      opcional: true,
      conteudo: (
        <>
          <Campo bloco rotulo="CR" dica="Opcional. Usado para agrupar por centro de resultado.">
            <AutoCompleteUnico
              selecionado={
                form.cr
                  ? {
                      codigo: form.cr.codigo,
                      rotulo: form.cr.descricaoCompleta || form.cr.descricao || '—',
                      secundario: String(form.cr.codigo ?? ''),
                    }
                  : null
              }
              // O registro inteiro vai em `cr`, como antes.
              aoEscolher={(o) => setForm((f) => ({ ...f, cr: o ? o.dado : null }))}
              buscar={buscarCrs}
              titulo="Selecionar CR"
              placeholder="Descrição do CR (Enter ou lupa)"
              vazio="Nenhum CR encontrado."
            />
          </Campo>
        </>
      ),
    },
  ];

  // Os dois ultimos so existem na EDICAO, e sao ferramenta, nao campo do
  // cadastro - por isso `opcional`, para nao travarem o finalizar.
  /* TROCAR SENHA SO NO PROPRIO CADASTRO (11/09/2026, a pedido).

     A aba aparecia em QUALQUER usuario aberto, e o formulario pede "Senha
     atual" - de quem? Editando outra pessoa, a leitura natural e "a senha
     atual dela", que quem administra nao tem; e quem digitasse a propria ali
     estaria tentando autenticar com credencial que nao corresponde ao cadastro
     aberto. O `POST /users/trocasenha/:codigo` manda o codigo do usuario da
     tela, entao a confusao tinha consequencia real, nao era so de rotulo.

     A regra e simples e verificavel: mesmo codigo do usuario logado. Cadastro
     novo (sem codigo) tambem nao mostra - nao ha senha atual de quem ainda nao
     existe.

     ISTO NAO E CONTROLE DE ACESSO, e sim remocao de uma opcao sem sentido no
     contexto: quem impede a troca indevida e o backend, que exige a senha
     atual. Se um dia existir "redefinir a senha de outro usuario", sera outra
     acao, com outro nome e outra rota - nao esta. */
  const ehMeuCadastro = Boolean(original.codigo) && String(original.codigo) === String(user?.codigo);

  if (ehMeuCadastro) {
    passos.push({
      id: 'seguranca',
      titulo: 'Segurança',
      grupo: 'Ferramentas',
      resumo: 'trocar minha senha',
      opcional: true,
      conteudo: (
      <>
        <Campo rotulo="Senha atual">
          <input
            type="password"
            value={senhaAtual}
            onChange={(e) => setSenhaAtual(e.target.value)}
            autoComplete="current-password"
            className={classesEntrada}
          />
        </Campo>
        <Campo rotulo="Nova senha">
          <input
            type="password"
            value={senhaNova}
            onChange={(e) => setSenhaNova(e.target.value)}
            autoComplete="new-password"
            className={classesEntrada}
          />
        </Campo>
        <Campo rotulo="Confirmar nova senha">
          <input
            type="password"
            value={senhaConfirmar}
            onChange={(e) => setSenhaConfirmar(e.target.value)}
            autoComplete="new-password"
            className={cn(
              classesEntrada,
              senhaConfirmar && senhaConfirmar !== senhaNova && 'border-danger'
            )}
          />
          {senhaConfirmar && senhaConfirmar !== senhaNova && (
            <span className="mt-1 block text-[11px] text-danger">As duas senhas não conferem.</span>
          )}
        </Campo>

        {/* Botao proprio: a troca de senha é uma requisicao SEPARADA
            (POST /users/trocasenha/:codigo) e nao entra no salvamento do
            cadastro - juntar as duas faria "Salvar" fazer duas coisas. */}
        <button
          type="button"
          onClick={alterarSenha}
          disabled={trocandoSenha || !senhaAtual || !senhaNova || senhaNova !== senhaConfirmar}
          className="h-9 w-full rounded-lg bg-primary text-[12.5px] font-bold text-primary-foreground disabled:opacity-40"
        >
          {trocandoSenha ? 'Alterando...' : 'Alterar senha'}
        </button>

        <p className="text-[11px] text-faint">
          Esta ação é independente do botão Finalizar — ela grava na hora.
        </p>
      </>
      ),
    });
  }

  passos.push({
    id: 'historico',
    titulo: 'Histórico',
    grupo: 'Ferramentas',
    informativo: true,
    conteudo: original.codigo ? (
      <HistoricoGPS tipo={'USUARIO'} chave={original.codigo} />
    ) : (
      <div className="rounded-lg border border-line bg-surface-2 p-4 text-[12px] text-muted-foreground">
          O histórico começa a ser gravado quando este cadastro for salvo. Cada alteração
          posterior fica registrada aqui com quem fez, quando e o que mudou.
        </div>
    ),
  });

  const subtitulo = editando
    ? [original.nome, original.email].filter(Boolean).join(' · ')
    : 'Novo usuário';

  return (
    <ModalPassos
      aberto={aberto}
      titulo={editando ? 'Editar usuário' : 'Novo usuário'}
      subtitulo={carregando ? 'Carregando...' : subtitulo}
      /* Editando, o cabecalho mostra QUEM esta sendo editado - avatar de iniciais,
         selos de estado e a linha de dado conferivel. Criando, `nome` fica de fora
         e o cabecalho volta a "Novo usuario": nao existe registro ainda, e um
         avatar de iniciais vazias seria enfeite. */
      nome={editando ? form.nome?.trim() || original.nome : undefined}
      selos={
        editando ? (
          <>
            <PillStatus ativo={statusTexto(form.status) === 'ATIVO'} />
            {form.perfil ? (
              <span className="rounded bg-soft px-1.5 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">
                {form.perfil}
              </span>
            ) : null}
          </>
        ) : undefined
      }
      meta={
        editando ? (
          <>
            {form.email || original.email}
            {original.codigo ? ` | código ${original.codigo}` : ''}
          </>
        ) : undefined
      }
      passos={passos}
      rotuloFinalizar={editando ? 'Salvar' : 'Criar usuário'}
      onFinalizar={salvar}
      onFechar={onFechar}
      salvando={salvando}
    />
  );
}
